import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTenant } from '@/contexts'
import { usePricingMutations } from '@/hooks/usePricing'
import type { PolicyScope } from '@/types/pricing'

export function PolicyCreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { locations } = useTenant()
  const mutations = usePricingMutations()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [scope, setScope] = useState<PolicyScope>('location')
  const [ownerLocationId, setOwnerLocationId] = useState('')

  const isSingleLocation = (locations?.length ?? 0) <= 1

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutations.createPolicy.mutate(
      {
        name,
        description: description || undefined,
        scope,
        ownerLocationId: scope === 'location' ? ownerLocationId : undefined,
      },
      {
        onSuccess: () => {
          onOpenChange(false)
          setName('')
          setDescription('')
          setScope('location')
          setOwnerLocationId('')
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Pricing Policy</DialogTitle>
            <DialogDescription>
              A policy is a named rate card. After creating it, add versions with rates.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="policy-name">Policy Name</Label>
              <Input
                id="policy-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Standard Rates, Weekend Premium"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="policy-description">Description (optional)</Label>
              <Textarea
                id="policy-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this policy covers..."
                rows={2}
              />
            </div>
            {!isSingleLocation && (
              <div className="grid gap-2">
                <Label>Scope</Label>
                <Select value={scope} onValueChange={(v) => setScope(v as PolicyScope)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="org">Organization-wide (shareable)</SelectItem>
                    <SelectItem value="location">Location-specific</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {scope === 'org'
                    ? 'Can be assigned to any location in your organization.'
                    : 'Owned by one location, not shareable.'}
                </p>
              </div>
            )}
            {scope === 'location' && (
              <div className="grid gap-2">
                <Label>Location</Label>
                <Select value={ownerLocationId} onValueChange={setOwnerLocationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location..." />
                  </SelectTrigger>
                  <SelectContent>
                    {locations?.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                mutations.createPolicy.isPending ||
                !name ||
                (scope === 'location' && !ownerLocationId)
              }
            >
              {mutations.createPolicy.isPending ? 'Creating...' : 'Create Policy'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
